import { ElementState, Preview } from "@creatomate/preview";

import { Organization, Story, Template } from "chq";

import { IMAGE_SLIDE_DURATION, STORY_TRANSITION_TIME } from "../constants";
import { createImageSlideShowStory } from "../../sidebar/tabs/template/implementors/default/montages/imageSlideshowTemplate";
import { createImageSlideShowWithCaptionsStory } from "../../sidebar/tabs/template/implementors/default/montages/imageSlideshowWithCaptionsTemplate";
import { createStorySlideStory } from "../../sidebar/tabs/template/implementors/default/montages/setStorySlideshowTemplate";
import { createMicrosoftAmplifySlide } from "../../sidebar/tabs/template/implementors/microsoft/montages/microsoftAmplifyTemplate";
import { createIntuitiveNameAndTitleOverlaySlide } from "../../sidebar/tabs/template/implementors/intuitive/montages/intuitiveNameandTitleOverlay";
import { createSantanderLandscapeWhiteWordmarkLogoOutroAnimation } from "../../sidebar/tabs/template/implementors/santander/montages/SantanderLandscapeWhiteWordmarkLogoOutroAnimation";
import { createSantanderLandscapewRedWordmarkLogoOutro } from "../../sidebar/tabs/template/implementors/santander/montages/SantanderLandscapewRedWordmarkLogoOutro";
import { createSantanderLandscapewWhiteWordmarkLogoOutro } from "../../sidebar/tabs/template/implementors/santander/montages/SantanderLandscapewWhiteWordmarkLogoOutro";
import { createSantanderLandscapewRedLogoOutro } from "../../sidebar/tabs/template/implementors/santander/montages/SantanderLandscapewRedLogoOutro";
import { createSantander4_5wWhiteLogoOutro } from "../../sidebar/tabs/template/implementors/santander/montages/Santander4_5wWhiteLogoOutro";
import { createSantander4_5wRedLogoOutro } from "../../sidebar/tabs/template/implementors/santander/montages/Santander4_5wRedLogoOutro";
import { createIrcMontageTemplate } from "../../sidebar/tabs/template/implementors/irc/montages/IrcMontageTemplate";
import { createIrcMontageTemplateNoIntro } from "../../sidebar/tabs/template/implementors/irc/montages/IrcMontageTemplateNoIntro";
import getDefaultSource from "./getDefaultSource";

export const OV4_DIFF = 0.78;
export const OV2_DIFF = 0.5507;
export const OV1_DIFF = 0.3406;

const addStoryToMontageTemplate = async (
  template: Template,
  preview: Preview,
  story: Story,
  index: number,
  organization: Organization
): Promise<string | undefined> => {
  const { media: { mediaType, duration = 0, height = 1, width = 1 } } = story;
  const storyDuration = mediaType === "video" ? duration : IMAGE_SLIDE_DURATION;
  const heightProportion = height / (preview.state?.height || 720);
  const mediaWidth = (100 * (width / heightProportion)) / (preview.state?.width || 1280);
  const elements = preview.state?.elements;

  let modifications: Record<string, any> = {};
  let outroElement: ElementState | undefined;
  let elementId: string | undefined;
  let time = outroElement?.globalTime || preview.state?.duration || 0;

  let outroOverlayIdentifierOne;
  let outroOverlayIdentifierTwo;
  let outroOverlayIdentifierFour;

  const syncOutroTime = (
    element: ElementState | undefined,
    duration: number,
    time: number,
    extraModifications: Record<string, number> = {}
  ) => {
    if (!element) return;

    modifications = {
      ...modifications,
      [`${element.source.id}.time`]: time + duration,
      ...extraModifications
    };
  };

  switch (template.name) {
    case "Image Slideshow":
      elementId = await createImageSlideShowStory(
        preview, template, story, storyDuration, mediaWidth, index, time
      );
      break;
    case "Image Slideshow with captions":
      elementId = await createImageSlideShowWithCaptionsStory(
        preview, template, story, storyDuration, mediaWidth, index, time
      );
      break;
    case "Story Slideshow":
      outroElement = elements?.find(element => element.source.name === "Logo Outro");
      time = outroElement?.globalTime || time;
      elementId = await createStorySlideStory(preview, template, story, storyDuration, index, time);
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Microsoft Amplify Template w/ White Outro":
      outroElement = elements?.find(element => element.source.name === "white_outro");
      time = outroElement?.globalTime || time;
      elementId = await createMicrosoftAmplifySlide(preview, template, story, storyDuration, time);
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Santander Landscape w/ White Wordmark Logo Outro and animation":
      outroElement = elements?.find(element => element.source.name === "white_wordmark_logo_outro");
      time = outroElement?.globalTime || time;
      outroOverlayIdentifierOne = elements?.find(element => element.source.name === "name_and_title_overlay_1");
      outroOverlayIdentifierTwo = elements?.find(element => element.source.name === "name_and_title_overlay_2");
      outroOverlayIdentifierFour = elements?.find(element => element.source.name === "name_and_title_overlay_4");
      elementId = await createSantanderLandscapeWhiteWordmarkLogoOutroAnimation(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time, {
        [`${outroOverlayIdentifierOne?.source?.id}.time`]: time + storyDuration - OV1_DIFF,
        [`${outroOverlayIdentifierTwo?.source?.id}.time`]: time + storyDuration - OV2_DIFF,
        [`${outroOverlayIdentifierFour?.source?.id}.time`]: time + storyDuration - OV4_DIFF
      });
      break;
    case "Santander Landscape w/ Red Wordmark Logo Outro":
      outroElement = elements?.find(element => element.source.name === "red_wordmark_logo_outro");
      time = outroElement?.globalTime || time;
      elementId = await createSantanderLandscapewRedWordmarkLogoOutro(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Santander Landscape w/ White Wordmark Logo Outro":
      outroElement = elements?.find(element => element.source.name === "white_wordmark_logo_outro");
      time = outroElement?.globalTime || time;
      elementId = await createSantanderLandscapewWhiteWordmarkLogoOutro(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Santander Landscape w/ Red Logo Outro":
      outroElement = elements?.find(element => element.source.name === "red_logo_outro");
      time = outroElement?.globalTime || time;
      elementId = await createSantanderLandscapewRedLogoOutro(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Santander 4:5 w/ White Logo Outro":
      outroElement = elements?.find(element => element.source.name === "white_logo_outro");
      time = outroElement?.globalTime || time;
      elementId = await createSantander4_5wWhiteLogoOutro(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Santander 4:5 w/ Red Logo Outro":
      outroElement = elements?.find(element => element.source.name === "red_logo_outro");
      time = outroElement?.globalTime || time;
      elementId = await createSantander4_5wRedLogoOutro(
        preview, template, story, storyDuration, time
      );
      syncOutroTime(outroElement, storyDuration, time);
      break;
    case "Intuitive name and title overlay":
      elementId = await createIntuitiveNameAndTitleOverlaySlide(
        preview, template, story, storyDuration, time
      );
      break;
    case "IRC Montage":
      // eslint-disable-next-line no-case-declarations
      let referenceElement = preview.state?.elements.filter(element => (
        element.source.source?.startsWith("https://")
      )).sort((firstElement, secondElement) => (
        firstElement.globalTime - secondElement.globalTime
      )).slice(-1)[0];

      if (!referenceElement) {
        referenceElement = preview.state?.elements.find(element => (
          element.source.name === "Intro - Header"
        ));
      }
      if (!referenceElement) break;

      time = (referenceElement.globalTime + referenceElement.duration) - STORY_TRANSITION_TIME;
      elementId = await createIrcMontageTemplate(
        preview, template, story, storyDuration, time, true
      );

      // outroElement = elements?.find(element => element.source.name === "Outro - Text");
      // syncOutroTime(outroElement, storyDuration, time);
      break;
    case "IRC Montage (no intro)":
      // eslint-disable-next-line no-case-declarations
      let referenceElementNoIntro = preview.state?.elements.filter(element => (
          element.source.source?.startsWith("https://")
      )).sort((firstElement, secondElement) => (
        firstElement.globalTime - secondElement.globalTime
      )).slice(-1)[0];

      if (!referenceElementNoIntro) {
        referenceElementNoIntro = preview.state?.elements.find(element => (
          element.source.name === "Name_1"
        ));
      }
      if (!referenceElementNoIntro) break;

      time = (referenceElementNoIntro.globalTime + referenceElementNoIntro.duration)
      - STORY_TRANSITION_TIME;
      elementId = await createIrcMontageTemplateNoIntro(
        preview, template, story, storyDuration, time, true
      );
      break;
    default:
      await preview.setSource(await getDefaultSource({ organization, preview }));
      break;
  }

  modifications.duration = storyDuration + (preview.state?.duration || 0) - 1;
  await preview.applyModifications(modifications);

  return elementId;
};

export default addStoryToMontageTemplate;
